/**
 * Leads slice - single source for leads API
 */
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../api/config';
import { toast } from 'react-toastify';
import { LEADS, SHAREABLE_LINKS, USERS, STATUSES, LOOKUP } from '../../api/endpoints';

export const getLeads = createAsyncThunk(
  'leads/getLeads',
  async (params = {}, { rejectWithValue }) => {
    try {
      const { data } = await api.get(LEADS.LIST, { params });
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Error fetching leads');
    }
  }
);

export const getLeadById = createAsyncThunk(
  'leads/getLeadById',
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await api.get(LEADS.BY_ID(id));
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to fetch lead');
    }
  }
);

export const importLeads = createAsyncThunk(
  'leads/importLeads',
  async (formData, { rejectWithValue }) => {
    try {
      const { data } = await api.post(LEADS.IMPORT, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Leads imported successfully!');
      return data;
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error importing leads');
      return rejectWithValue(err.response?.data?.message || 'Error importing leads');
    }
  }
);

export const createLead = createAsyncThunk(
  'leads/createLead',
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await api.post(LEADS.LIST, payload);
      toast.success('Lead created successfully!');
      return data;
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error creating lead');
      return rejectWithValue(err.response?.data?.message || 'Error creating lead');
    }
  }
);

export const updateLead = createAsyncThunk(
  'leads/updateLead',
  async ({ id, data: updateData }, { rejectWithValue }) => {
    try {
      const { data } = await api.put(LEADS.BY_ID(id), updateData);
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to update lead');
    }
  }
);

export const deleteLead = createAsyncThunk(
  'leads/deleteLead',
  async (leadId, { rejectWithValue }) => {
    try {
      const { data } = await api.delete(LEADS.BY_ID(leadId));
      toast.success('Lead deleted successfully!');
      return { ...data, leadId };
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error deleting lead');
      return rejectWithValue(err.response?.data?.message || 'Error deleting lead');
    }
  }
);

export const updateLeadStatus = createAsyncThunk(
  'leads/updateLeadStatus',
  async ({ leadId, payload }, { rejectWithValue }) => {
    try {
      const { data } = await api.put(LEADS.UPDATE_STATUS(leadId), payload);
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to update lead status');
    }
  }
);

/** Assign one or more users to a lead. Pass userIds array; payload is always { users: [id1, id2, ...] }. */
export const assignLeadToUser = createAsyncThunk(
  'leads/assignLeadToUser',
  async ({ leadId, userIds }, { rejectWithValue }) => {
    try {
      const ids = Array.isArray(userIds) ? userIds : [];
      if (!ids.length) return rejectWithValue('No user(s) provided');
      const { data } = await api.post(LEADS.ASSIGN_USER(leadId), { users: ids });
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to assign lead');
    }
  }
);

export const createShareableLink = createAsyncThunk(
  'leads/createShareableLink',
  async (leadId, { rejectWithValue }) => {
    try {
      const { data } = await api.post('/shareable-links', { lead_id: leadId });
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to create shareable link');
    }
  }
);

export const getLeadAssignedUsers = createAsyncThunk(
  'leads/getLeadAssignedUsers',
  async (leadId, { rejectWithValue }) => {
    try {
      const { data } = await api.get(LEADS.ASSIGNED_USERS(leadId));
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to fetch assigned users');
    }
  }
);

export const getLeadAttachments = createAsyncThunk(
  'leads/getLeadAttachments',
  async (leadId, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`/leads/${leadId}`);
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to fetch attachments');
    }
  }
);

export const downloadLeadFile = createAsyncThunk(
  'leads/downloadLeadFile',
  async (path, { rejectWithValue }) => {
    try {
      const response = await api.post(LEADS.DOWNLOAD, { path }, { responseType: 'blob' });
      return { data: response.data, headers: response.headers };
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Download failed');
    }
  }
);

export const createCustomerFromLead = createAsyncThunk(
  'leads/createCustomerFromLead',
  async (customerData, { rejectWithValue }) => {
    try {
      const { data } = await api.post('/users', customerData);
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err);
    }
  }
);

// Lookup thunks (statuses, sources, brands)
export const getLeadsStatus = createAsyncThunk(
  'leads/getLeadsStatus',
  async (params = {}, { rejectWithValue }) => {
    try {
      const { data } = await api.get(STATUSES.LIST, { params });
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Error fetching statuses');
    }
  }
);

export const getLeadSources = createAsyncThunk(
  'leads/getLeadSources',
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await api.get(LOOKUP.SOURCES);
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Error fetching sources');
    }
  }
);

export const getLeadBrands = createAsyncThunk(
  'leads/getLeadBrands',
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await api.get(LOOKUP.BRANDS);
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Error fetching brands');
    }
  }
);

const initialState = {
  leads: null,
  currentLead: null,
  assignedUsers: null,
  statuses: null,
  sources: null,
  brands: null,
  loading: false,
  error: null,
};

const leadsSlice = createSlice({
  name: 'leads',
  initialState,
  reducers: {
    clearCurrentLead: (state) => {
      state.currentLead = null;
    },
    clearLeadsError: (state) => {
      state.error = null;
    },
    updateLeadInList: (state, { payload }) => {
      const { leadId, updates } = payload;
      if (state.leads?.data?.list) {
        const idx = state.leads.data.list.findIndex((l) => l.id === leadId);
        if (idx >= 0) state.leads.data.list[idx] = { ...state.leads.data.list[idx], ...updates };
      } else if (state.leads?.list) {
        const idx = state.leads.list.findIndex((l) => l.id === leadId);
        if (idx >= 0) state.leads.list[idx] = { ...state.leads.list[idx], ...updates };
      }
    },
    setLeadsList: (state, { payload }) => {
      const updater = payload;
      const list = state.leads?.data?.list ?? state.leads?.list;
      if (Array.isArray(list) && typeof updater === 'function') {
        const newList = updater(list);
        if (state.leads?.data) state.leads.data.list = newList;
        else if (state.leads) state.leads.list = newList;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getLeads.fulfilled, (state, action) => {
        state.loading = false;
        state.leads = action.payload;
      })
      .addCase(getLeadById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentLead = action.payload;
      })
      .addCase(getLeadAssignedUsers.fulfilled, (state, action) => {
        state.assignedUsers = action.payload;
      })
      .addCase(getLeadsStatus.fulfilled, (state, action) => {
        state.statuses = action.payload;
      })
      .addCase(getLeadSources.fulfilled, (state, action) => {
        state.sources = action.payload;
      })
      .addCase(getLeadBrands.fulfilled, (state, action) => {
        state.brands = action.payload;
      })
      .addMatcher(
        (a) => [
          getLeads.pending,
          getLeadById.pending,
          createLead.pending,
          updateLead.pending,
          deleteLead.pending,
        ].some((t) => t.match(a)),
        (state) => {
          state.loading = true;
          state.error = null;
        }
      )
      .addMatcher(
        (a) => [
          getLeads.rejected,
          getLeadById.rejected,
          createLead.rejected,
          updateLead.rejected,
          deleteLead.rejected,
        ].some((t) => t.match(a)),
        (state, action) => {
          state.loading = false;
          state.error = action.payload || 'Something went wrong';
        }
      )
      .addMatcher(
        (a) =>
          [createLead.fulfilled, updateLead.fulfilled, deleteLead.fulfilled].some((t) =>
            t.match(a)
          ),
        (state) => {
          state.loading = false;
        }
      );
  },
});

export const { clearCurrentLead, clearLeadsError, updateLeadInList, setLeadsList } =
  leadsSlice.actions;
export default leadsSlice.reducer;
